import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { Response } from "../types";

import { ENDPOINTS } from "@/api/application-api";
import { ToastType } from "@/enums";
import { Report } from "@/types/evaluate";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayToast } from "@/utils/toast";
import { useAuth } from "@/context/auth-context";

interface DuplicateEvaluationResponse {
  success: boolean;
  evaluation_id: number;
  evaluation: Report;
}

export const useDuplicateEvaluation = (
  onSuccessCallback?: (data: DuplicateEvaluationResponse) => void,
) => {
  const authContext = useAuth();
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();

  return useMutation<
    Response<DuplicateEvaluationResponse, "evaluation", string>,
    Error,
    { evaluationId: number; selectedWorkspaceId: number; folderId?: number }
  >({
    mutationFn: async ({ evaluationId, selectedWorkspaceId, folderId }) => {
      const url = new URL(ENDPOINTS.duplicate_evaluation(evaluationId));

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: jsonAuthHeaders(authContext?.userToken ?? ""),
        body: JSON.stringify({
          workspace_id: selectedWorkspaceId,
          ...(folderId !== undefined && { folder_id: folderId }),
        }),
      });

      return await handleResponse(response);
    },
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports]);
        queryClient.invalidateQueries(["reportsForPrompt"]);
        queryClient.invalidateQueries([ENDPOINTS.get_reports(Number(workspaceId))]);

        displayToast(`Evaluation duplicated successfully`, ToastType.success);

        if (onSuccessCallback && response.evaluation) {
          onSuccessCallback({
            success: response.success,
            evaluation_id: (response.evaluation as unknown as Report).id,
            evaluation: response.evaluation as unknown as Report,
          });
        }
      }
    },
    onError: error => {
      displayToast(error.message || "Failed to duplicate evaluation", ToastType.error);
    },
  });
};
